/**
 * Saved delivery addresses (part of the universal profile).
 *   <AddressBook />                       — manage on the Profile page
 *   <AddressPicker value onChange />      — choose (or add) one at checkout
 */
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { axiosInstance } from '../../config/axios';
import { useAuth } from '../../context/AuthContext';
import { formatIndianMobile, isValidIndianMobile } from '../../lib/phone';

const inputCls = 'w-full border-2 border-gray-100 focus:border-[#2B3A67] rounded-xl px-3 py-2.5 text-sm outline-none';

const STATES = [
  'Andaman and Nicobar Islands', 'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chandigarh',
  'Chhattisgarh', 'Dadra and Nagar Haveli and Daman and Diu', 'Delhi', 'Goa', 'Gujarat', 'Haryana',
  'Himachal Pradesh', 'Jammu and Kashmir', 'Jharkhand', 'Karnataka', 'Kerala', 'Ladakh', 'Lakshadweep',
  'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Puducherry',
  'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand',
  'West Bengal',
];

export function formatAddress(a) {
  if (!a) return '';
  return [a.line1, a.line2, a.landmark, `${a.city}, ${a.state} ${a.pincode}`].filter(Boolean).join(', ');
}

export function useAddresses() {
  const [addresses, setAddresses] = useState(null);
  const reload = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/auth/addresses');
      setAddresses(res.data || []);
      return res.data || [];
    } catch {
      setAddresses([]);
      return [];
    }
  }, []);
  useEffect(() => { reload(); }, [reload]);
  return { addresses, reload };
}

export function AddressForm({ initial, onSaved, onCancel }) {
  const { user } = useAuth();
  const [a, setA] = useState(() => ({
    label: '', full_name: user?.name || '', phone: user?.mobile_number || '', line1: '', line2: '',
    landmark: '', city: user?.city || '', state: '', pincode: '', is_default: false, ...(initial || {}),
  }));
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setA((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!a.full_name.trim() || !a.line1.trim() || !a.city.trim() || !a.state) {
      toast.error('Fill in name, address, city and state'); return;
    }
    if (!isValidIndianMobile(a.phone)) { toast.error('Enter a valid 10-digit phone number'); return; }
    if (!/^[1-9]\d{5}$/.test(a.pincode)) { toast.error('Enter a valid 6-digit PIN code'); return; }
    setSaving(true);
    try {
      const body = { ...a };
      delete body.id;
      const res = initial?.id
        ? await axiosInstance.put(`/auth/addresses/${initial.id}`, body)
        : await axiosInstance.post('/auth/addresses', body);
      onSaved(res.data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(Array.isArray(d) ? (d[0]?.msg || 'Check the address') : (d || 'Could not save the address'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="address-form">
      <div className="grid grid-cols-2 gap-3">
        <input className={inputCls} placeholder="Recipient name *" value={a.full_name}
          onChange={(e) => set('full_name', e.target.value)} data-testid="address-name" />
        <input className={inputCls} type="tel" placeholder="Phone *" value={a.phone}
          onChange={(e) => set('phone', formatIndianMobile(e.target.value))} data-testid="address-phone" />
      </div>
      <input className={inputCls} placeholder="House / flat, building, street *" value={a.line1}
        onChange={(e) => set('line1', e.target.value)} data-testid="address-line1" />
      <input className={inputCls} placeholder="Area, locality" value={a.line2 || ''}
        onChange={(e) => set('line2', e.target.value)} data-testid="address-line2" />
      <input className={inputCls} placeholder="Landmark (optional)" value={a.landmark || ''}
        onChange={(e) => set('landmark', e.target.value)} />
      <div className="grid grid-cols-2 gap-3">
        <input className={inputCls} placeholder="City *" value={a.city}
          onChange={(e) => set('city', e.target.value)} data-testid="address-city" />
        <input className={inputCls} inputMode="numeric" maxLength={6} placeholder="PIN code *" value={a.pincode}
          onChange={(e) => set('pincode', e.target.value.replace(/\D/g, ''))} data-testid="address-pincode" />
      </div>
      <select className={inputCls} value={a.state} onChange={(e) => set('state', e.target.value)} data-testid="address-state">
        <option value="">State *</option>
        {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
      <div className="flex items-center gap-2 flex-wrap">
        {['Home', 'Work', 'Other'].map((l) => (
          <button type="button" key={l} onClick={() => set('label', a.label === l ? '' : l)}
            className={`px-3 py-1.5 rounded-full text-xs font-bold border ${a.label === l ? 'bg-[#2B3A67] text-white border-[#2B3A67]' : 'border-gray-200 text-gray-500'}`}>
            {l}
          </button>
        ))}
        <label className="ml-auto flex items-center gap-2 text-xs text-gray-600">
          <input type="checkbox" checked={!!a.is_default} onChange={(e) => set('is_default', e.target.checked)} />
          Default address
        </label>
      </div>
      <div className="flex gap-2">
        {onCancel && (
          <button type="button" onClick={onCancel} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-bold text-gray-600">
            Cancel
          </button>
        )}
        <button type="button" onClick={save} disabled={saving}
          className="flex-1 py-2.5 rounded-xl bg-[#2B3A67] text-white text-sm font-bold disabled:opacity-50" data-testid="address-save">
          {saving ? 'Saving…' : 'Save address'}
        </button>
      </div>
    </div>
  );
}

function AddressCard({ a, selected, onSelect, actions }) {
  return (
    <div className={`rounded-xl border-2 p-3 ${selected ? 'border-[#2B3A67] bg-[#2B3A67]/5' : 'border-gray-100'}`}
      onClick={onSelect} role={onSelect ? 'radio' : undefined} aria-checked={onSelect ? selected : undefined}
      style={{ cursor: onSelect ? 'pointer' : 'default' }} data-testid="address-card">
      <div className="flex items-start gap-2">
        {onSelect && <i className={`ph-bold ${selected ? 'ph-radio-button text-[#2B3A67]' : 'ph-circle text-gray-300'} mt-0.5`} />}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-gray-800">
            {a.full_name}
            {a.label && <span className="ml-2 text-[10px] uppercase tracking-wider bg-gray-100 text-gray-500 rounded px-1.5 py-0.5">{a.label}</span>}
            {a.is_default && <span className="ml-1 text-[10px] uppercase tracking-wider bg-green-50 text-green-700 rounded px-1.5 py-0.5">Default</span>}
          </p>
          <p className="text-xs text-gray-600 mt-0.5">{formatAddress(a)}</p>
          <p className="text-xs text-gray-400 mt-0.5">{a.phone}</p>
        </div>
      </div>
      {actions && <div className="flex gap-3 mt-2 pl-6 text-xs font-bold">{actions}</div>}
    </div>
  );
}

export function AddressBook() {
  const { addresses, reload } = useAddresses();
  const [editing, setEditing] = useState(null);   // null | 'new' | address

  const remove = async (a) => {
    if (!window.confirm('Remove this address?')) return;
    try { await axiosInstance.delete(`/auth/addresses/${a.id}`); reload(); } catch { toast.error('Could not remove it'); }
  };
  const makeDefault = async (a) => {
    try { await axiosInstance.post(`/auth/addresses/${a.id}/default`); reload(); } catch { toast.error('Could not update it'); }
  };

  if (addresses === null) return <div className="h-16 animate-pulse bg-gray-50 rounded-xl" />;
  return (
    <div className="space-y-3" data-testid="address-book">
      {addresses.length === 0 && !editing && (
        <p className="text-sm text-gray-500">No saved addresses yet. Add one for faster checkout.</p>
      )}
      {addresses.map((a) => (editing?.id === a.id ? (
        <AddressForm key={a.id} initial={a} onCancel={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); toast.success('Address updated'); }} />
      ) : (
        <AddressCard key={a.id} a={a} actions={<>
          <button type="button" className="text-[#2B3A67]" onClick={() => setEditing(a)}>Edit</button>
          {!a.is_default && <button type="button" className="text-[#2B3A67]" onClick={() => makeDefault(a)}>Make default</button>}
          <button type="button" className="text-red-500" onClick={() => remove(a)}>Remove</button>
        </>} />
      )))}
      {editing === 'new' ? (
        <AddressForm onCancel={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); toast.success('Address saved'); }} />
      ) : !editing && (
        <button type="button" onClick={() => setEditing('new')}
          className="w-full py-2.5 rounded-xl border-2 border-dashed border-gray-200 text-sm font-bold text-[#2B3A67]"
          data-testid="add-address-btn">
          <i className="ph-bold ph-plus mr-1" /> Add address
        </button>
      )}
    </div>
  );
}

export function AddressPicker({ value, onChange }) {
  const { addresses, reload } = useAddresses();
  const [adding, setAdding] = useState(false);

  // Pre-select the default address
  useEffect(() => {
    if (addresses?.length && !addresses.some((a) => a.id === value)) onChange(addresses[0].id);
  }, [addresses, value, onChange]);

  if (addresses === null) return <div className="h-16 animate-pulse bg-gray-50 rounded-xl" />;
  return (
    <div className="space-y-2" role="radiogroup" aria-label="Delivery address" data-testid="address-picker">
      {addresses.map((a) => (
        <AddressCard key={a.id} a={a} selected={a.id === value} onSelect={() => onChange(a.id)} />
      ))}
      {adding || addresses.length === 0 ? (
        <div className="rounded-xl border border-gray-100 p-3">
          <p className="text-xs font-bold text-gray-500 mb-2">NEW ADDRESS</p>
          <AddressForm onCancel={addresses.length ? () => setAdding(false) : null}
            onSaved={async (saved) => { setAdding(false); await reload(); onChange(saved.id); }} />
        </div>
      ) : (
        <button type="button" onClick={() => setAdding(true)}
          className="w-full py-2 rounded-xl border-2 border-dashed border-gray-200 text-xs font-bold text-[#2B3A67]"
          data-testid="picker-add-address">
          <i className="ph-bold ph-plus mr-1" /> Deliver to a new address
        </button>
      )}
    </div>
  );
}
